import { create } from 'zustand'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'
import type { Profile } from '../lib/database.types'

interface AuthState {
  user: User | null
  profile: Profile | null
  session: Session | null
  initialized: boolean
  loading: boolean
  pendingUsers: Profile[]

  isAdmin: () => boolean
  isApproved: () => boolean
  isAuthenticated: () => boolean
  initialize: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  signUpWithEmail: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>
  signInWithOAuth: (provider: 'google' | 'github' | 'apple') => Promise<{ error: Error | null }>
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>
  fetchPendingUsers: () => Promise<void>
  approveUser: (userId: string) => Promise<{ error: Error | null }>
  rejectUser: (userId: string) => Promise<{ error: Error | null }>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  session: null,
  initialized: false,
  loading: false,
  pendingUsers: [],

  isAdmin: () => get().profile?.role === 'admin',
  isApproved: () => get().profile?.approved === true,
  isAuthenticated: () => !!get().user,

  initialize: async () => {
    // Already initialized? Skip
    if (get().initialized) return

    if (!isSupabaseConfigured()) {
      set({ initialized: true })
      return
    }

    try {
      // 1. Get current session FIRST
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        // Try to get profile (don't fail if missing)
        let profile = null
        try {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle()
          profile = data
        } catch (e) {
          // Profile fetch failed, continue without it
        }

        set({ user: session.user, session, profile, initialized: true })
      } else {
        set({ initialized: true })
      }

      // 2. Set up listener for FUTURE auth changes only
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_OUT') {
          set({ user: null, session: null, profile: null })
        } else if (event === 'SIGNED_IN' && session?.user) {
          // Fetch profile async, update state
          try {
            const { data } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .maybeSingle()
            set({ user: session.user, session, profile: data })
          } catch {
            set({ user: session.user, session, profile: null })
          }
        }
      })
    } catch (error) {
      set({ initialized: true })
    }
  },

  signInWithEmail: async (email, password) => {
    set({ loading: true })
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })

    if (!error && data.session?.user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.session.user.id)
        .maybeSingle()

      set({ user: data.session.user, session: data.session, profile, loading: false })
    } else {
      set({ loading: false })
    }

    return { error: error as Error | null }
  },

  signUpWithEmail: async (email, password, fullName) => {
    set({ loading: true })
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName }
      }
    })

    if (!error && data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        email,
        full_name: fullName || null
      } as never)
    }

    set({ loading: false })
    return { error: error as Error | null }
  },

  signInWithOAuth: async (provider) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard/`
      }
    })
    return { error: error as Error | null }
  },

  signInWithMagicLink: async (email) => {
    set({ loading: true })
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard/`
      }
    })
    set({ loading: false })
    return { error: error as Error | null }
  },

  signOut: async () => {
    await supabase.auth.signOut()
    set({ user: null, session: null, profile: null })
  },

  updateProfile: async (updates) => {
    const user = get().user
    if (!user) return { error: new Error('Not authenticated') }

    const { error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', user.id)

    if (!error) {
      set(state => ({
        profile: state.profile ? { ...state.profile, ...updates } : null
      }))
    }

    return { error: error as Error | null }
  },

  fetchPendingUsers: async () => {
    const profile = get().profile
    if (profile?.role !== 'admin') return

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('approved', false)
      .order('created_at', { ascending: false })

    set({ pendingUsers: data || [] })
  },

  approveUser: async (userId: string) => {
    const profile = get().profile
    if (profile?.role !== 'admin') return { error: new Error('Not authorized') }

    const { error } = await supabase
      .from('profiles')
      .update({ approved: true, updated_at: new Date().toISOString() } as never)
      .eq('id', userId)

    if (!error) {
      set(state => ({
        pendingUsers: state.pendingUsers.filter(u => u.id !== userId)
      }))
    }

    return { error: error as Error | null }
  },

  rejectUser: async (userId: string) => {
    const profile = get().profile
    if (profile?.role !== 'admin') return { error: new Error('Not authorized') }

    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId)

    if (!error) {
      set(state => ({
        pendingUsers: state.pendingUsers.filter(u => u.id !== userId)
      }))
    }

    return { error: error as Error | null }
  }
}))
